import { toCamelCase } from '@libs/util';
import { TextGroup } from './models';
/**
 * Grouping logic mirrors datahub's `datahub/texts/helpers.ts` `TextTypesGroupMap` — pure client-side
 * text-name-to-group classification, not backend-dependent, so it's real logic even though the domain
 * around it is EFKT-MOCK.
 */
const TEXT_GROUP_BY_NAME = {
    adText: TextGroup.Ads,
    areaDescription: TextGroup.AreaDescription,
    areaHeadline: TextGroup.AreaDescription,
    bannerHeadline: TextGroup.Ads,
    bannerText: TextGroup.Ads,
    caseDescription: TextGroup.HeadingAndIntroduction,
    caseHeadline: TextGroup.HeadingAndIntroduction,
    description: TextGroup.HeadingAndIntroduction,
    facebookHeadline: TextGroup.SocialMedia,
    facebookSubheadline: TextGroup.SocialMedia,
    facebookText: TextGroup.SocialMedia,
    googleHeadline: TextGroup.SocialMedia,
    googleText: TextGroup.SocialMedia,
    headline: TextGroup.HeadingAndIntroduction,
    inputForSpeakFilm: TextGroup.SpeechMovieText,
    inputTextFilm: TextGroup.SpeechMovieText,
    instagramText: TextGroup.SocialMedia,
    mainText: TextGroup.HeadingAndIntroduction,
    poiHotspotText: TextGroup.POI,
    sectionDescription: TextGroup.Section,
    sectionHeadline: TextGroup.Section,
    speakTextFilm: TextGroup.SpeechMovieText,
    speakTextSlideshow: TextGroup.SpeechMovieText,
    subHeadLine: TextGroup.HeadingAndIntroduction,
    twitterText: TextGroup.SocialMedia,
    usp: TextGroup.USP,
};
function getGroupByTextName(name) {
    // Strip a single trailing digit before lookup (e.g. "sectionHeadline2" -> "sectionHeadline"),
    // matching datahub's separateIndexFromString behavior for the common case.
    const stripped = name.replace(/\d$/, '');
    return TEXT_GROUP_BY_NAME[stripped] || TextGroup.Other;
}
/** Mirrors datahub's `datahub/texts/helpers.ts::separateIndexFromString` exactly, same translation keys. */
function separateIndexFromString(input = '') {
    const camelTitle = !input.match(/[\s_-]/g) ? input : toCamelCase(input);
    const indexPos = camelTitle.search(/[0-9]/g);
    const index = ~indexPos ? camelTitle.charAt(indexPos) : 0;
    const string = index ? camelTitle.replace(index, '') : camelTitle;
    return { string, index: +index };
}
export function mapCaseTextFromDto({ name, title, content }) {
    const { string: normalizedTitle, index: titleIndex } = separateIndexFromString(title);
    return { name, title, content, normalizedTitle, titleIndex };
}
/**
 * `GET /api/text-sections` response -> the full template, every group present with blank-content
 * placeholder texts. Mirrors datahub's `datahub/texts/helpers.ts::initializeTextGroups` exactly —
 * same grouping, same blank `content`, just built from `ITextSectionDto` instead of the datahub dto.
 */
export function initializeTextGroups(sections) {
    const initialGroups = Object.values(TextGroup).map(value => ({
        groupName: value,
        texts: [],
    }));
    return sections
        .reduce((acc, section) => {
        const textGroupName = getGroupByTextName(section.name);
        const foundGroupIndex = acc.findIndex(({ groupName }) => groupName === textGroupName);
        if (foundGroupIndex !== -1) {
            const { string: normalizedTitle, index: titleIndex } = separateIndexFromString(section.title);
            acc[foundGroupIndex].texts.push({
                content: '',
                title: section.title,
                name: section.name,
                normalizedTitle,
                titleIndex,
            });
        }
        return acc;
    }, initialGroups)
        .filter(group => group.texts.length > 0);
}
export function groupCaseTextsFromDto(dtoTexts) {
    const groups = new Map();
    dtoTexts.forEach(dtoText => {
        var _a;
        const group = getGroupByTextName(dtoText.name);
        const texts = (_a = groups.get(group)) !== null && _a !== void 0 ? _a : [];
        texts.push(mapCaseTextFromDto(dtoText));
        groups.set(group, texts);
    });
    return Array.from(groups.entries()).map(([groupName, texts]) => ({ groupName, texts }));
}
